import { Children, Fragment, isValidElement, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode, type Ref } from "react";
import { Select, type SelectOption } from "rizzui";
import { cn } from "@steelyard/theme";

/**
 * Every dropdown in apps/admin: rizzui's searchable `Select` (2026-09-28, the project owner's
 * request), a drop-in for `@steelyard/theme`'s native `SelectField` — same props, same `<option>`
 * children — so a screen swaps the import and nothing else. The rizzui control is COPIED from
 * vendor/isomorphic/packages/isomorphic-core/src/components/controlled-table/status-field.tsx.
 *
 * Every caller keeps working unchanged because a hidden native `<select>` still carries the field:
 * `{...register("x")}`'s ref and onChange, a `name` + `defaultValue` read from `FormData` on submit,
 * or a controlled `value` + `onChange`. Choosing in the visible control sets that select's value and
 * fires its `change` event, which is exactly what a person choosing in it would do. The native
 * select is out of the tab order and the accessibility tree; the visible control is the one
 * labelled. It carries no `required` of its own (the form's schema validates, and the browser's own
 * bubble would point at a control nobody can see); the label still shows the asterisk.
 *
 * An `<option value="">` is the placeholder ("Choose a brand…"): it becomes the closed control's
 * placeholder text and is NOT listed as a choice. Where empty is a real choice ("No parent",
 * "Unit default"), mark it `<option value="" data-choice>` and it stays in the list.
 */

type OptionElement = ReactElement<{ value?: string | number; disabled?: boolean; children?: ReactNode; "data-choice"?: boolean }>;
type Option = SelectOption & { value: string; label: string; disabled?: boolean; choice?: boolean };

/** Flattens `<option>`s, including those inside fragments and mapped arrays, to plain options. */
function optionsOf(children: ReactNode): Option[] {
  const out: Option[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) {
      out.push(...optionsOf((child as ReactElement<{ children?: ReactNode }>).props.children));
      return;
    }
    if (child.type !== "option") return;
    const { value, disabled, children: text, "data-choice": choice } = (child as OptionElement).props;
    // A tree option is indented with leading spaces; keep them visible (HTML would collapse them).
    const label = Children.toArray(text).join("").replace(/^ +/, (spaces) => "  ".repeat(spaces.length));
    out.push({ value: String(value ?? label), label, disabled, choice: Boolean(choice) });
  });
  return out;
}

export function SelectField({
  label,
  name,
  error,
  hint,
  id: idOverride,
  className,
  wrapperClassName,
  children,
  required,
  disabled,
  disabledReason,
  hideLabel,
  value,
  defaultValue,
  onChange,
  ref,
  ...select
}: {
  label: string;
  name: string;
  error?: string;
  hint?: ReactNode;
  id?: string;
  /** Classes for the label+control+hint column — e.g. `col-span-full` inside a `FormGroup` grid. */
  wrapperClassName?: string;
  /** Why this select is disabled, shown in place of `hint` while it is. */
  disabledReason?: ReactNode;
  /** Keep the label for screen readers only — for a table cell whose column header already names it. */
  hideLabel?: boolean;
  ref?: Ref<HTMLSelectElement>;
} & Omit<React.ComponentProps<"select">, "ref">) {
  const id = idOverride ?? `field-${name}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const effectiveHint = disabled && disabledReason ? disabledReason : hint;
  const allOptions = optionsOf(children);
  const placeholder = allOptions.find((option) => option.value === "")?.label ?? "Select…";
  const options = allOptions.filter((option) => option.value !== "" || option.choice);

  const native = useRef<HTMLSelectElement | null>(null);
  const [current, setCurrent] = useState(String(value ?? defaultValue ?? ""));

  // react-hook-form writes the registered value into the native select from its ref, and a reset
  // does too, with no event — so the visible control re-reads it after every render. Deliberately
  // no dependency list: what changed is the DOM, which no dependency can name; the equality check
  // stops it from looping.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (native.current && native.current.value !== current) setCurrent(native.current.value);
  });

  const choose = (next: string) => {
    const el = native.current;
    if (!el) return;
    el.value = next;
    el.dispatchEvent(new Event("change", { bubbles: true }));
    setCurrent(next);
  };

  const setRefs = (el: HTMLSelectElement | null) => {
    native.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) (ref as { current: HTMLSelectElement | null }).current = el;
  };

  const selected = options.find((option) => option.value === current);

  return (
    <div className={wrapperClassName}>
      <select
        ref={setRefs}
        name={name}
        value={value}
        defaultValue={value === undefined ? defaultValue : undefined}
        disabled={disabled}
        onChange={(event) => {
          setCurrent(event.target.value);
          onChange?.(event);
        }}
        tabIndex={-1}
        aria-hidden
        className="sr-only"
        {...select}
      >
        {children}
      </select>
      <Select<Option>
        id={id}
        label={
          <>
            {label}
            {required ? (
              <span aria-hidden="true" className="text-red">
                {" "}
                *
              </span>
            ) : null}
          </>
        }
        labelClassName={hideLabel ? "sr-only" : "mb-1.5 text-sm font-medium"}
        options={options}
        // A plain string value, as every template Select passes: rizzui compares it by identity,
        // and an option object rebuilt on each render would never be "the same" one.
        value={current}
        onChange={(next: unknown) => choose(String(next))}
        getOptionValue={(option) => option.value}
        displayValue={() => selected?.label ?? ""}
        placeholder={placeholder}
        searchable
        searchPlaceHolder="Search…"
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errorId : null, effectiveHint ? hintId : null].filter(Boolean).join(" ") || undefined}
        dropdownClassName="p-1.5"
        optionClassName="h-9"
        // The error message is this component's own `role="alert"` line below; rizzui only draws the border.
        selectClassName={cn(className, error && "!border-red !ring-red")}
      />
      {effectiveHint ? (
        <p id={hintId} className="mt-1.5 text-xs text-gray-500">
          {effectiveHint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-[13px] text-red">
          {error}
        </p>
      ) : null}
    </div>
  );
}
